import { InstrumentationBase } from '@opentelemetry/instrumentation';
import type { InstrumentationConfig } from '@opentelemetry/instrumentation';
import {
  context,
  propagation,
  trace,
  SpanKind,
  SpanStatusCode,
} from '@opentelemetry/api';
import type { Span } from '@opentelemetry/api';

export interface WebSocketInstrumentationConfig extends InstrumentationConfig {
  ignoreUrls?: (string | RegExp)[];
  propagateTraceHeaderWebSocketUrls?: (string | RegExp)[];
}

function urlMatches(url: string, patterns: (string | RegExp)[]): boolean {
  return patterns.some((pattern) => {
    if (typeof pattern === 'string') {
      return url.includes(pattern);
    }
    pattern.lastIndex = 0;
    return pattern.test(url);
  });
}

function getStringByteLength(str: string): number {
  let byteLength = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code <= 0x7f) byteLength += 1;
    else if (code <= 0x7ff) byteLength += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      byteLength += 4;
      i++;
    } else byteLength += 3;
  }
  return byteLength;
}

function getMessageSize(data: unknown): number {
  if (typeof data === 'string') return getStringByteLength(data);
  if (data instanceof ArrayBuffer) return data.byteLength;
  if (data instanceof Blob) return data.size;
  if (ArrayBuffer.isView(data)) return data.byteLength;
  return 0;
}

function getMessageType(data: unknown): string {
  return typeof data === 'string' ? 'text' : 'binary';
}

let connectionCounter = 0;
function generateConnectionId(): string {
  return `ws_${Date.now()}_${++connectionCounter}`;
}

const NORMAL_CLOSE_CODES = new Set([1000, 1001]);
function isAbnormalCloseCode(code: number): boolean {
  return !NORMAL_CLOSE_CODES.has(code);
}

export class WebSocketInstrumentation extends InstrumentationBase {
  private _originalWebSocket: typeof WebSocket | null = null;

  constructor(config: WebSocketInstrumentationConfig = {}) {
    super('opentelemetry-fe-websocket', '0.1.0', config);
  }

  protected init(): void {}

  enable(): void {
    if (typeof globalThis.WebSocket === 'undefined') return;
    if (this._originalWebSocket !== null) return;

    this._originalWebSocket = globalThis.WebSocket;
    const OriginalWebSocket = this._originalWebSocket;
    const getConfig = (): WebSocketInstrumentationConfig =>
      this.getConfig() as WebSocketInstrumentationConfig;
    const getTracer = () => this.tracer;
    const instrumentInstance = (
      ws: WebSocket,
      url: string,
      connectionSpan: Span
    ): void => this._instrumentInstance(ws, url, connectionSpan);

    function PatchedWebSocket(
      url: string | URL,
      protocols?: string | string[]
    ): WebSocket {
      const urlString = typeof url === 'string' ? url : url.toString();
      const config = getConfig();

      if (urlMatches(urlString, config.ignoreUrls ?? [])) {
        return new OriginalWebSocket(url, protocols);
      }

      const connectionSpan = getTracer().startSpan('ws.connection', {
        kind: SpanKind.CLIENT,
      });

      let constructUrl: string | URL = url;
      if (
        urlMatches(urlString, config.propagateTraceHeaderWebSocketUrls ?? [])
      ) {
        const carrier: Record<string, string> = {};
        propagation.inject(
          trace.setSpan(context.active(), connectionSpan),
          carrier
        );
        const traceparent = carrier['traceparent'];
        if (traceparent) {
          const separator = urlString.includes('?') ? '&' : '?';
          constructUrl = `${urlString}${separator}traceparent=${encodeURIComponent(
            traceparent
          )}`;
        }
      }

      let instance: WebSocket;
      try {
        instance = new OriginalWebSocket(constructUrl, protocols);
      } catch (err) {
        connectionSpan.recordException(
          err instanceof Error ? err : new Error(String(err))
        );
        connectionSpan.setStatus({ code: SpanStatusCode.ERROR });
        connectionSpan.end();
        throw err;
      }
      instrumentInstance(instance, urlString, connectionSpan);
      return instance;
    }

    PatchedWebSocket.prototype = OriginalWebSocket.prototype;
    PatchedWebSocket.CONNECTING = OriginalWebSocket.CONNECTING;
    PatchedWebSocket.OPEN = OriginalWebSocket.OPEN;
    PatchedWebSocket.CLOSING = OriginalWebSocket.CLOSING;
    PatchedWebSocket.CLOSED = OriginalWebSocket.CLOSED;
    Object.setPrototypeOf(PatchedWebSocket, OriginalWebSocket);

    globalThis.WebSocket = PatchedWebSocket as unknown as typeof WebSocket;
  }

  disable(): void {
    if (this._originalWebSocket !== null) {
      globalThis.WebSocket = this._originalWebSocket;
      this._originalWebSocket = null;
    }
  }

  private _instrumentInstance(
    ws: WebSocket,
    url: string,
    connectionSpan: Span
  ): void {
    const tracer = this.tracer;
    let hadError = false;
    let connectionEnded = false;
    const connectionId = generateConnectionId();

    const sharedAttributes = {
      'websocket.url': url,
      'websocket.connection_id': connectionId,
    };

    connectionSpan.setAttributes(sharedAttributes);

    let unloadHandler: (() => void) | null = null;

    const endConnectionSpan = (
      code: number,
      reason: string,
      status: SpanStatusCode
    ): void => {
      if (connectionEnded) return;
      connectionEnded = true;
      connectionSpan.setAttributes({
        'websocket.close_code': code,
        'websocket.close_reason': reason,
      });
      connectionSpan.setStatus({ code: status });
      connectionSpan.end();
      if (unloadHandler !== null) {
        window.removeEventListener('pagehide', unloadHandler);
        unloadHandler = null;
      }
    };

    unloadHandler = (): void => {
      endConnectionSpan(1001, 'page unload', SpanStatusCode.OK);
    };
    window.addEventListener('pagehide', unloadHandler);

    ws.addEventListener('open', () => {
      connectionSpan.addEvent('open');
    });

    ws.addEventListener('error', () => {
      hadError = true;
    });

    ws.addEventListener('close', (event: CloseEvent) => {
      endConnectionSpan(
        event.code,
        event.reason,
        hadError || isAbnormalCloseCode(event.code)
          ? SpanStatusCode.ERROR
          : SpanStatusCode.OK
      );
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      const span = tracer.startSpan('ws.receive', {
        kind: SpanKind.CONSUMER,
        attributes: {
          ...sharedAttributes,
          'websocket.message.size': getMessageSize(event.data),
          'websocket.message.type': getMessageType(event.data),
        },
      });
      span.setStatus({ code: SpanStatusCode.OK });
      span.end();
    });

    const originalSend = ws.send.bind(ws);
    ws.send = (
      data: string | ArrayBufferLike | Blob | ArrayBufferView
    ): void => {
      const span = tracer.startSpan('ws.send', {
        kind: SpanKind.CLIENT,
        attributes: {
          ...sharedAttributes,
          'websocket.message.size': getMessageSize(data),
          'websocket.message.type': getMessageType(data),
        },
      });
      try {
        originalSend(data);
        span.setStatus({ code: SpanStatusCode.OK });
      } catch (err) {
        span.recordException(
          err instanceof Error ? err : new Error(String(err))
        );
        span.setStatus({ code: SpanStatusCode.ERROR });
        throw err;
      } finally {
        span.end();
      }
    };
  }
}
